import { Component, Input, Output, EventEmitter, forwardRef, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-date-picker-with-clear',
    styleUrls: ['./date-picker-with-clear.component.scss'],
    template: `
    <div class="date-picker-wrapper">
      <span class="date-input-box">
      <input
        type="date"
        [class]="datePickerClass + ' iso-date-input'"
        [required]="required"
        [attr.min]="minDateString"
        [attr.max]="maxDateString"
        (click)="onDatePickerClick()"
        (keydown)="onKeyDown($event)"
        (paste)="onPaste($event)"
        (drop)="onDrop($event)"
        (beforeinput)="onBeforeInput($event)"
        [ngModel]="value"
        (ngModelChange)="onValueChange($event)"
        (change)="onDateChange($event)" />
        <span class="date-display" aria-hidden="true">{{ displayValue }}</span>
      </span>
      
      @if (hasDate) {
<button
        type="button"
       
        (click)="onClearClick()"
        class="clear-date-btn"
        [disabled]="isClearDisabled"
        [title]="'Clear Date'">
        <span class="Appkit4-icon icon-close-outline"></span>
      </button>
}
    </div>
  `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerWithClearComponent),
            multi: true
        }
    ],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DatePickerWithClearComponent implements ControlValueAccessor {
  constructor(private elementRef: ElementRef) {}
  // Input properties for ap-date-picker
  @Input() format: string = 'yyyy-MM-DD';
  @Input() type: string = 'singleCalendar';
  @Input() size: string = 'medium';
  @Input() minDate: Date | string = '';
  @Input() maxDate: Date | string = '';
  @Input() isTitleLeft: boolean = true;
  @Input() datePickerClass: string = '';
  @Input() required: boolean = false;
  
  // Input for clear button disabled state
  @Input() clearDisabledCondition: (value: any) => boolean = (value) => !value || value === '' || value === null || value === undefined;
  
  // Output events
  @Output() dateChange = new EventEmitter<any>();
  @Output() datePickerClick = new EventEmitter<void>();
  @Output() clearClick = new EventEmitter<void>();
  
  // Internal value
  private _value: any = '';
  
  // ControlValueAccessor implementation
  private onChange = (value: any) => {};
  private onTouched = () => {};
  
  get value(): any {
    return this._value;
  }
  
  set value(val: any) {
    this._value = val;
    this.onChange(val);
  }
  
  get isClearDisabled(): boolean {
    return this.clearDisabledCondition(this._value);
  }

  get hasDate(): boolean {
    if (this._value === null || this._value === undefined || this._value === '') {
      return false;
    }
    // Handle Date objects
    if (this._value instanceof Date) {
      return !isNaN(this._value.getTime());
    }
    // Handle date strings
    if (typeof this._value === 'string') {
      return this._value.trim() !== '';
    }
    return true;
  }

  /** Native date inputs render in the browser locale; show the ISO value (yyyy-mm-dd) as in Dev. */
  get displayValue(): string {
    if (!this.hasDate) {
      return '';
    }
    if (this._value instanceof Date) {
      return this.toLocalISOString(this._value);
    }
    return String(this._value).substring(0, 10);
  }

  get minDateString(): string | null {
    if (!this.minDate) {
      return null;
    }
    if (this.minDate instanceof Date) {
      return this.toLocalISOString(this.minDate);
    }
    return String(this.minDate);
  }

  get maxDateString(): string | null {
    if (!this.maxDate) {
      return null;
    }
    if (this.maxDate instanceof Date) {
      return this.toLocalISOString(this.maxDate);
    }
    return String(this.maxDate);
  }

  private toLocalISOString(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  
  // ControlValueAccessor methods
  writeValue(value: any): void {
    if (value instanceof Date && !isNaN(value.getTime())) {
      this._value = this.toLocalISOString(value);
    } else {
      this._value = value;
    }
  }
  
  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }
  
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  
  setDisabledState(isDisabled: boolean): void {
    // Handle disabled state if needed
  }
  
  // Event handlers
  onDateChange(event: any): void {
    const input = event?.target as HTMLInputElement | null;
    const immediate = input?.value || '';
    if (immediate) {
      this.publishDate(immediate);
      return;
    }
    // The native picker sometimes fires change before the value is committed.
    setTimeout(() => {
      if (!input?.isConnected) {
        return;
      }
      this.publishDate(input.value || '');
    }, 0);
  }

  onValueChange(value: any): void {
    // An empty input event is the picker clearing mid-commit, not a user clear.
    // The clear button sets the value itself.
    if (value === '' || value === null || value === undefined) {
      return;
    }
    this.value = value;
  }

  private publishDate(dateStr: string): void {
    if (!dateStr) {
      return;
    }
    if (this._value !== dateStr) {
      this.value = dateStr;
    }
    this.dateChange.emit({
      startDate: { format: () => dateStr },
      endDate: { format: () => dateStr }
    });
  }
  
  onDatePickerClick(): void {
    this.datePickerClick.emit();
  }

  // Block all keyboard editing so the value can only be changed via the
  // native date picker UI or the clear button. Allow Tab so users can still
  // navigate fields with the keyboard.
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      return;
    }
    event.preventDefault();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
  }

  onBeforeInput(event: InputEvent): void {
    // The native date picker commits through insertReplacementText.
    // Cancelling that event leaves the previous date in place, so a second
    // edit looks successful and then saves the old value.
    if (event.inputType === 'insertReplacementText') {
      return;
    }
    event.preventDefault();
  }
  
  onClearClick(): void {
    this.value = null;
    this.clearClick.emit();
    
    // Force change detection
    // setTimeout(() => {
    //   this.forceClearInput();
    // }, 0);
  }
  
  /**
   * Force clear the input field using DOM manipulation
   * This is a fallback for stubborn date picker components
   */
//   private forceClearInput(): void {
//     try {
//       // Find the input field within this component
//       const inputElement = this.elementRef.nativeElement.querySelector('input');
//       if (inputElement && inputElement.value) {
//         // Clear the input value and trigger change event
//         inputElement.value = '';
//         inputElement.dispatchEvent(new Event('input', { bubbles: true }));
//         inputElement.dispatchEvent(new Event('change', { bubbles: true }));
//       }
//     } catch (error) {
//       console.warn('Error clearing date picker input:', error);
//     }
//   }
}
